'use client';

import { ContactPlacement, PLACEMENT_ATTRIBUTE } from '@/lib/analytics';
import Divider from '@/components/divider';
import FullWidthSection from '@/components/full-width-section';
import ImageOptimizer from '@/components/image-optimizer';
import MapDetail from '@/components/map-detail';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { BranchBreadcrumbItem, HomeBreadcrumbItem } from '@/data/globals.data';
import { fromSlug } from '@/lib/utils';
import { useStoreDetail } from '@/services/query/store.query';
import { useModalStore } from '@/store/modal.store';
import { Coordinates } from '@/types/map.type';
import { MarkerType } from '@/types/marker.type';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import BranchConvenienceSection from './components/branch-convenience-section';
import EducatorProfile from './components/branch-educator-profile';
import BranchExtraInfoSection from './components/branch-extra-info-section';
import BranchHeader from './components/branch-header';
import BranchImageGallery from './components/branch-image-gallery';
import BranchInfoSection from './components/branch-info-section';
import BranchPaymentSection from './components/branch-payment-section';
import BranchReviewSection from './components/branch-review-section';
import ReviewModal from './components/modal/branch-review.modal';
import NearestBranches from './components/nearest-branches';
import RecommendedPlansSection from './components/recommended-plans-section';

export default function Client({
  isRecommendEntry = false,
}: {
  /** 학원 목록 추천 배너(`?from=academy`)로 들어왔는지(서버가 정한다) */
  isRecommendEntry?: boolean;
}) {
  const { open: openModal } = useModalStore();
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const params = useParams();
  const branchSlug = params?.branchSlug as string;
  const branchId = fromSlug(branchSlug);

  // 훅은 조건 없이 호출한다(잘못된 slug면 enabled:false로 요청만 막는다).
  // 서버에서 프리페치한 데이터가 있으면 이 훅이 곧바로 그 값을 돌려주어
  // 첫 렌더가 SSR HTML에 그대로 실린다.
  const { data: storeEntity, isLoading, error } = useStoreDetail(branchId ?? 0);

  if (!branchId) {
    return <div>잘못된 지점입니다.</div>;
  }

  const branchRaw = storeEntity?.data;

  const reviews = branchRaw?.reviews
    ? branchRaw.reviews.map((r) => ({
        id: r.id,
        reviewer: r.username,
        rating: 5,
        content: r.reviewText,
        createdAt: r.createdAt,
        images: r.images,
      }))
    : [];

  // 평균 평점 계산 (리뷰에 rating이 없으므로 5로 고정)
  const rating = 5;

  const markers: MarkerType[] = branchRaw
    ? [
        {
          loc: [
            typeof branchRaw.longitude === 'number' && branchRaw.longitude !== 0
              ? branchRaw.longitude
              : 127.0668649,
            typeof branchRaw.latitude === 'number' && branchRaw.latitude !== 0
              ? branchRaw.latitude
              : 37.5079838,
          ] as Coordinates,
          title: branchRaw.name || '지점',
        },
      ]
    : [];

  const branch = branchRaw && {
    ...branchRaw,
    keywords: branchRaw.keywords ? branchRaw.keywords.split('|') : [],
    convenience: branchRaw.convenience
      ? branchRaw.convenience
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [],
    paymentMethods: branchRaw.paymentInfo
      ? branchRaw.paymentInfo
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [],
    address: branchRaw.address ? [branchRaw.address] : [],
    hours: branchRaw.weeklyHours
      ? branchRaw.weeklyHours.map((h) => ({
          label: h.dayOfWeek,
          time:
            h.openTime && h.closeTime ? `${h.openTime} ~ ${h.closeTime}` : '',
        }))
      : [],
    reviews,
    extraAddress: branchRaw.summaryAddress || '',
    established: branchRaw.openingDate || '',
    // tags: branchRaw.keywords ? branchRaw.keywords.split('|') : [],
    tags: branchRaw.keywordTags ? branchRaw.keywordTags.split(',') : [],
    rating,
    signupCount: branchRaw.cumulativeSignups || 0,
    isDirect: branchRaw?.type === 'direct',
    markers,
    nearestBranches: branchRaw.nearby,
    phoneNumber: branchRaw.phoneNumber || '',
    convenienceTag: branchRaw.convenienceTag || [],
  };

  if (isLoading) {
    return <div>로딩중...</div>;
  }
  if (!branch || error) {
    return <div>지점을 찾을 수 없습니다.</div>;
  }

  // 기본 배치·추천 진입 전용 배치가 함께 쓰는 섹션
  const openReviewModal = () => setReviewModalOpen(true);
  const instructors = (branchRaw?.instructors || []).map((inst) => ({
    ...inst,
    id: String(inst.id),
    isAcademicInstructorCertified: Number(inst.isAcademicInstructorCertified),
    isPracticalInstructorCertified: Number(inst.isPracticalInstructorCertified),
    isExaminerCertified: Number(inst.isExaminerCertified),
  }));
  const infoSection = (
    <BranchInfoSection
      address={branch.address}
      roadAddress={branchRaw?.roadAddress || ''}
      hours={branch.hours}
      markers={branch.markers}
      phoneNumber={branch.phoneNumber}
      subways={branchRaw?.subways}
      operatingHoursNote={branchRaw?.operatingHoursNote || ''}
    />
  );
  const extraInfoSection = (
    <div className="flex flex-col">
      <BranchExtraInfoSection
        capacity={branchRaw?.maxCapacity}
        established={branch.established}
        phone={branch.phoneNumber}
      />
      <BranchConvenienceSection convenienceTag={branch.convenienceTag} />
      <BranchPaymentSection paymentMethods={branch.paymentMethods} />
    </div>
  );

  // 일반 진입: 기존 상세 배치(서버 렌더도 이 배치로 나간다)
  const defaultLayout = (
    <div className="desktop:flex-row desktop:justify-center desktop:gap-12 flex flex-col">
      <div className="desktop:flex desktop:w-[504px] desktop:gap-10 hidden flex-col">
        <BranchImageGallery images={branch.photoPaths} />
        <BranchReviewSection
          rating={branch.rating}
          reviews={branch.reviews}
          onMoreReviewClick={openReviewModal}
        />
      </div>
      <div className="desktop:w-[636px]">
        <BranchImageGallery
          images={branch.photoPaths}
          className="desktop:hidden"
        />
        <div className="desktop:hidden pb-7" />
        <BranchHeader
          name={branch.name}
          isDirect={branch.isDirect}
          tags={branch.tags}
          signupCount={branch.signupCount}
        />
        <div className="desktop:hidden pb-7" />

        <BranchReviewSection
          rating={branch.rating}
          reviews={branch.reviews}
          className="desktop:hidden"
          onMoreReviewClick={openReviewModal}
        />
        <div className="desktop:pb-9 pb-8" />
        <Divider />
        <div className="desktop:pb-9 pb-8" />

        {infoSection}
        {extraInfoSection}
        {/* 강사안내 */}
        <EducatorProfile instructors={instructors} />

        <div className="desktop:pb-9 pb-8" />
        <div className="desktop:hidden block">
          <Divider className="space-y-7" />
          <div className="mb-10 pb-10" />
        </div>
        <NearestBranches
          branches={branch.nearestBranches}
          className="desktop:hidden block"
        />
      </div>
    </div>
  );

  // 학원 목록 추천 배너 진입 전용 배치.
  // PC 우측은 "지점명 → 추천 요금제 → 강사 → 키워드"로 전환에 집중하고,
  // 좌측은 "사진 → 리뷰 → 지점안내"로 신뢰 정보를 받쳐 양쪽 높이를 맞춘다.
  // 모바일에서는 두 열 래퍼가 `contents`로 풀려 order 순서대로 한 줄에 쌓인다
  // (같은 섹션을 모바일용으로 한 번 더 렌더하지 않는다).
  const recommendLayout = (
    <div className="desktop:flex-row desktop:items-start desktop:justify-center desktop:gap-12 flex flex-col gap-9">
      <div className="desktop:flex desktop:w-[504px] desktop:flex-col desktop:gap-10 contents">
        <div className="desktop:order-none order-1">
          <BranchImageGallery images={branch.photoPaths} />
        </div>
        <div className="desktop:order-none order-4">
          <BranchReviewSection
            rating={branch.rating}
            reviews={branch.reviews}
            onMoreReviewClick={openReviewModal}
          />
        </div>
        <div className="desktop:order-none order-5">
          <Divider />
          <div className="desktop:pb-9 pb-8" />
          {infoSection}
          {extraInfoSection}
        </div>
      </div>

      <div className="desktop:flex desktop:w-[636px] desktop:flex-col contents">
        <div className="desktop:order-none order-2">
          <BranchHeader
            name={branch.name}
            isDirect={branch.isDirect}
            tags={branch.tags}
            signupCount={branch.signupCount}
          />
        </div>
        <div className="desktop:order-none order-3">
          <RecommendedPlansSection store={branchRaw} className="mt-0" />
        </div>
        {instructors.length > 0 && (
          <div className="desktop:order-none desktop:mt-10 order-6">
            {/* 바로 위 추천 요금제 박스가 경계를 그리므로 구분선은 생략한다 */}
            <EducatorProfile instructors={instructors} showTopDivider={false} />
          </div>
        )}
        <div className="desktop:hidden order-8">
          <Divider className="space-y-7" />
          <div className="mb-10 pb-10" />
          <NearestBranches branches={branch.nearestBranches} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/** 리뷰 상세 리스트 */}
      <ReviewModal
        storeId={branch.id}
        storeName={branch.name}
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
      >
        {/* children 추가 내용 */}
      </ReviewModal>
      <FullWidthSection>
        <MapDetail markers={branch.markers} />
      </FullWidthSection>
      <PageHeader
        breadcrumbItems={[
          HomeBreadcrumbItem,
          BranchBreadcrumbItem,
          { label: branch.name, href: `/branch/${branchSlug}` },
        ]}
      />
      {isRecommendEntry ? recommendLayout : defaultLayout}
      <div className="desktop:block desktop:web-w desktop:mx-auto hidden">
        <Divider className="space-y-7" />
        <div className="desktop:mb-[60px]" />
      </div>
      <NearestBranches
        branches={branch.nearestBranches}
        className="desktop:block hidden"
      />

      {/* <div className="desktop:block hidden">
        <BranchStickyBar />
      </div> */}

      {/** 하단 고정 버튼영역 */}
      <div className="mobile-w desktop:hidden fixed bottom-0 left-1/2 w-full -translate-x-1/2">
        {/* <FullWidthSection> */}
        <div className="relative flex items-center justify-center overflow-hidden py-[14px]">
          {/* 실제 배경 */}
          <div className="bg-level-2 absolute inset-0 opacity-85" />

          {/* 애니메이션 레이어 */}
          <div className="animate-progress bg-level-2 absolute inset-y-0 left-0 w-0" />

          {/* 콘텐츠 */}
          {/* <div className="relative flex w-full items-center justify-center gap-x-[min(9rem,10%)]"> */}
          <div className="relative flex w-full items-center justify-center gap-2.5">
            <a
              href={`tel:${branchRaw.phoneNumber}`}
              {...{ [PLACEMENT_ATTRIBUTE]: ContactPlacement.BRANCH_DETAIL }}
              className="relative h-[45px] w-[135px]"
            >
              <ImageOptimizer
                src="/images/branches/call_btn.png"
                alt="전화상담 버튼"
                fill
              />
            </a>
            <Button
              onClick={() => openModal(branchRaw)}
              className="relative h-[45px] w-[135px]"
            >
              <ImageOptimizer
                src="/images/branches/reserve_btn.png"
                alt="상담예약 버튼"
                fill
              />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
